import { useEffect, useMemo, useState } from "react";
import styles from "./AscActivityCrossword.module.scss";
import AscButton from "../../../../AscButton/AscButton";
import AscCrosswordPuzzle from "../../../../AscCrosswordPuzzle/AscCrosswordPuzzle";
import AscHeading from "../../../../AscTypography/AscHeading/AscHeading";
import AscParagraph from "../../../../AscTypography/AscParagraph/AscParagraph";
import {
  AdminCrosswordProps,
  AscCrosswordPuzzleWordScope,
} from "@ascendnow/shared";
import AscPuzzleQuestionAnswerForm from "../../../AscPuzzleQuestionAnswerForm/AscPuzzleQuestionAnswerForm";
import { useAscAddModuleActivitiesForm } from "../../useAscAddModuleActivitiesForm";

export default function AscActivityCrossword() {
  const formik = useAscAddModuleActivitiesForm();

  const wordsAcrossInit = formik?.values?.crossword?.questions?.acrossQuestions;
  const wordsDownInit = formik?.values?.crossword?.questions?.downQuestions;

  const initCrossword = {
    id: "",
    answer: "",
    beginningCellNumber: NaN,
    question: "",
  };

  const [wordsAcross, setWordsAcross] = useState<AdminCrosswordProps[]>(
    wordsAcrossInit?.length ? wordsAcrossInit : [initCrossword]
  );
  const [wordsDown, setWordsDown] = useState<AdminCrosswordProps[]>(
    wordsDownInit?.length ? wordsDownInit : [initCrossword]
  );

  const [loading, setLoading] = useState(true);

  function handleWordsAcrossChange(
    index: number,
    wordAcross: Partial<AdminCrosswordProps>
  ) {
    //reject if cell number is already taken, and not the same index
    if (
      wordAcross?.beginningCellNumber !== undefined &&
      (wordAcross?.beginningCellNumber < 0 ||
        wordAcross?.beginningCellNumber >
          Math.pow(formik.values?.crossword?.questions?.size || 10, 2) ||
        wordsAcross?.some(
          (w, i) =>
            w?.beginningCellNumber === wordAcross?.beginningCellNumber &&
            i !== index
        ))
    ) {
      return;
    }
    const newWords = [...wordsAcross];
    newWords[index] = { ...newWords[index], ...wordAcross };
    setWordsAcross(newWords);
  }

  function handleWordsDownChange(
    index: number,
    wordDown: Partial<AdminCrosswordProps>
  ) {
    //reject if cell number is already taken, and not the same index
    if (
      wordDown?.beginningCellNumber !== undefined &&
      (wordDown?.beginningCellNumber < 0 ||
        wordDown?.beginningCellNumber >
          Math.pow(formik.values.crossword?.questions?.size || 10, 2) ||
          wordsDown?.some(
          (w, i) =>
            w?.beginningCellNumber === wordDown?.beginningCellNumber &&
            i !== index
        ))
    ) {
      return;
    }
    const newWords = [...wordsDown];
    newWords[index] = { ...newWords[index], ...wordDown };
    setWordsDown(newWords);
  }

  const transformCrosswordValues = (
    wordsArray: AscCrosswordPuzzleWordScope[],
    orientation: "across" | "down"
  ) => {
    const transformedValues = wordsArray.map(
      ({ word, beginningCellNumber, ...rest }) => ({
        answer: word,
        question:
          orientation === "across"
            ? wordsAcross?.find(
                (w) => w.beginningCellNumber === beginningCellNumber
              )?.question
            : wordsDown?.find(
                (w) => w.beginningCellNumber === beginningCellNumber
              )?.question,
        beginningCellNumber,
        ...rest,
      })
    );

    return transformedValues;
  };

  function addNewWordAcross() {
    setWordsAcross([...wordsAcross, initCrossword]);
  }
  function addNewWordDown() {
    setWordsDown([...wordsDown, initCrossword]);
  }

  function handleDeleteWordAcross(index: number) {
    const newWords = [...wordsAcross];
    newWords.splice(index, 1);
    if (newWords.length === 0) {
      newWords.push(initCrossword);
    }
    setWordsAcross(newWords);
  }

  function handleDeleteWordDown(index: number) {
    const newWords = [...wordsDown];
    newWords.splice(index, 1);
    if (newWords.length === 0) {
      newWords.push(initCrossword);
    }
    setWordsDown(newWords);
  }

  function isPuzzleEmpty() {
    if (wordsAcross.length === 0 && wordsDown.length === 0) {
      return true;
    }
    if (
      wordsAcross?.every((w) => !w?.answer) &&
      wordsDown?.every((w) => !w?.answer)
    ) {
      return true;
    }
    return false;
  }

  const filteredWordsAcross = useMemo(
    () =>
      wordsAcross
        ?.filter(
          (w) => !!w?.answer || typeof w?.beginningCellNumber !== "number"
        )
        ?.map((w) => ({
          beginningCellNumber: w?.beginningCellNumber,
          word: w?.answer,
          clue: w?.question,
          id: w?.id,
        })),
    [wordsAcross]
  );
  const filteredWordsDown = useMemo(
    () =>
      wordsDown
        ?.filter(
          (w) => !!w?.answer || typeof w?.beginningCellNumber !== "number"
        )
        ?.map((w) => ({
          beginningCellNumber: w?.beginningCellNumber,
          word: w?.answer,
          clue: w?.question,
          id: w?.id,
        })),
    [wordsDown]
  );

  useEffect(() => {
    wordsAcrossInit?.length && setWordsAcross(wordsAcrossInit);

    wordsDownInit?.length && setWordsDown(wordsDownInit);

    setLoading(false);
  }, []);

  if (loading) {
    return null;
  }
  return (
    <div className={styles.ground}>
      <AscCrosswordPuzzle
        mode="create"
        size={formik.values?.crossword?.questions?.size}
        onGenerate={(words, size) => {
          formik.setFieldValue(
            "crossword.questions.acrossQuestions",
            transformCrosswordValues(words.wordsAcross, "across")
          );
          formik.setFieldValue(
            "crossword.questions.downQuestions",
            transformCrosswordValues(words.wordsDown, "down")
          );
          formik.setFieldValue("crossword.questions.size", size);
        }}
        disabled={isPuzzleEmpty()}
        // onError={(a) => (console.log("onError", a))}
        wordsAcross={filteredWordsAcross}
        wordsDown={filteredWordsDown}
      />
      <div className={styles.headerGround}>
        <AscHeading className={styles.header} level={6}>
          Questions & Answers
        </AscHeading>
      </div>

      <div className={styles.titleGround}>
        <AscParagraph className={styles.title}>Across</AscParagraph>

        <AscButton
          className={styles.link}
          variant="link"
          hideLinkIcon={true}
          onClick={addNewWordAcross}
        >
          Add question <span>+</span>
        </AscButton>
      </div>
      {wordsAcross.map((word, index) => (
        <AscPuzzleQuestionAnswerForm
          key={`question_across_${index}`}
          onChange={(wordAcross) => handleWordsAcrossChange(index, wordAcross)}
          value={word}
          canDelete
          onDelete={() => handleDeleteWordAcross(index)}
        />
      ))}

      <div className={styles.titleGround}>
        <AscParagraph className={styles.title}>Down</AscParagraph>

        <AscButton
          className={styles.link}
          variant="link"
          hideLinkIcon={true}
          onClick={addNewWordDown}
        >
          Add question <span>+</span>
        </AscButton>
      </div>

      {wordsDown.map((word, index) => (
        <AscPuzzleQuestionAnswerForm
          key={`question_down_${index}`}
          onChange={(wordDown) => handleWordsDownChange(index, wordDown)}
          value={word}
          canDelete
          onDelete={() => handleDeleteWordDown(index)}
        />
      ))}
    </div>
  );
}
